import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authApi from '../services/authApi';
import securityApi from '../services/securityApi';
import useSessionKeepalive from '../hooks/use-session-keepalive';
import useFayeErrorHandler from '../hooks/use-faye-error-handler';
import useFayeSubscription from '../hooks/use-faye-subscription';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const data = await authApi.getMe();
      if (data && data.isLoggedIn) {
        setUser(data);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('[AuthContext] Failed to fetch /api/me:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const logout = () => {
    securityApi.logout();
  };

  const refreshUser = useCallback(() => {
    return fetchMe();
  }, [fetchMe]);

  // Patches the cached user in place (no refetch) — used for optimistic
  // updates like avatar uploads, where the caller already has the new value
  // and a refetch would race the backend's write.
  const patchUser = useCallback((partial) => {
    setUser(prev => (prev ? { ...prev, ...partial } : prev));
  }, []);

  // Step 1 of email login: request a passcode.
  const requestLoginCode = useCallback((email) => {
    return authApi.requestCode(email);
  }, []);

  // Step 2 of email login: verify the passcode, then refresh auth state.
  const verifyLoginCode = useCallback(async (email, code) => {
    await authApi.verifyCode(email, code);
    await fetchMe();
  }, [fetchMe]);

  useSessionKeepalive(!!user);
  useFayeErrorHandler(user?.username);

  // Keeps the cached user in sync across tabs/sessions and covers the case
  // where the avatar-upload's own optimistic patch (in UserProfile.js) beat
  // the backend's write — the authoritative broadcast corrects it either way.
  useFayeSubscription(user?.username ? `/person-data/${user.username}` : null, (receivedMessage) => {
    if (!receivedMessage?.message) return;
    try {
      const message = JSON.parse(receivedMessage.message);
      if (message.messageType === 'set-avatar') {
        patchUser({ profilePic: message.avatar });
      }
    } catch (e) {
      console.error('[AuthContext] Failed to parse Faye message:', e);
    }
  }, [user?.username]);

  const value = {
    user,
    loading,
    isLoggedIn: !!user,
    isAdmin: user?.isAdmin || false,
    isDeveloper: user?.isDeveloper || false,
    isSprintManager: user?.isSprintManager || false,
    canManageSprints: user?.isAdmin || user?.isSprintManager || false,
    username: user?.username || '',
    companyName: user?.companyName || '',
    logout,
    requestLoginCode,
    verifyLoginCode,
    refreshUser,
    patchUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export default AuthContext;
